import React from "react";

import { useTranslation } from "utils";

import { Icon, LazyLoadImage, Link, Svg } from "components";

interface BulletPointsProps {
  items: string[] | undefined;
}

interface IntroScreenProps {
  farmerType: FarmerType;
}

interface IntroScreenNativeMobileProps {
  className?: string;
  farmerType: FarmerType;
}

export type FarmerType = "dairy" | "default";

const BulletPoints = ({ items }: BulletPointsProps) => {
  if (!Array.isArray(items)) return null;
  return (
    <>
      {items.map((item, index) => (
        <div data-testid={`intro-screen-checkpoint-${index}`} className="intro-screen-list-items" key={index}>
          <Icon className="intro-screen-list-items-icon" size="m" iconName="tabler/circle-check" />
          <h4 className="margin-left-50">{item}</h4>
        </div>
      ))}
    </>
  );
};

const BulletPointsMobile = ({ items }: BulletPointsProps) => {
  if (!Array.isArray(items)) return null;
  return (
    <>
      {items.map((item, index) => (
        <div data-testid={`intro-screen-checkpoint-${index}`} className="intro-screen-native-mobile-list-items" key={index}>
          <Icon className="intro-screen-native-mobile-list-items-icon" size="s" iconName="tabler/circle-check" />
          <p className="paragraph-m intro-screen-native-mobile-list-items-text">{item}</p>
        </div>
      ))}
    </>
  );
};

const getIntroImage = (farmerType: FarmerType): string => {
  const imageMap: Record<FarmerType, string> = {
    dairy: "https://klimpublic.s3.eu-central-1.amazonaws.com/dairy-signup.webp",
    default: `${process.env.REACT_APP_FARM_URL}/images/intro/split_screen_intro.jpg`,
  };

  return imageMap[farmerType];
};

const getIntroImageMobile = (farmerType: FarmerType): string => {
  const imageMap: Record<FarmerType, string> = {
    dairy: "https://klimpublic.s3.eu-central-1.amazonaws.com/dairy-signup.webp",
    default: `${process.env.REACT_APP_FARM_URL}/images/intro/split_screen_intro_mobile.png`,
  };

  return imageMap[farmerType];
};

export const IntroScreen = ({ farmerType }: IntroScreenProps) => {
  const { t } = useTranslation("intro");

  const getHeadline = () => t(`intro.headline.${farmerType}`);

  const getMessages = () => t(`intro.messages.${farmerType}`, { returnObjects: true }) as string[];

  return (
    <div className="intro-screen">
      <LazyLoadImage alt="" src={getIntroImage(farmerType)} />
      <div className="container">
        <Link className="margin-bottom-250" to="/" mixpanelText="klim-logo">
          <Svg svgName="logos/klim_logo_text_black" width={75} height={32} />
        </Link>
        <h1 className="margin-bottom-250">{getHeadline()}</h1>
        <BulletPoints items={getMessages()} />
      </div>
    </div>
  );
};

export const IntroScreenNativeMobile = ({ className, farmerType }: IntroScreenNativeMobileProps) => {
  const { t } = useTranslation("intro");

  const getHeadline = () => {
    const key = farmerType === "dairy" ? "intro.headline.dairy" : "intro.headline";
    return t(key);
  };

  const getMessages = () => {
    const key = farmerType === "dairy" ? "intro.messages.dairy" : "intro.messages";
    return t(key, { returnObjects: true }) as string[];
  };

  return (
    <div className={`intro-screen-native-mobile ${className || ""}`}>
      <div className="intro-screen-native-mobile-container">
        <LazyLoadImage alt="" src={getIntroImageMobile(farmerType)} />
        <div className="content">
          <h1 className="margin-bottom-200">{getHeadline()}</h1>
          <BulletPointsMobile items={getMessages()} />
        </div>
      </div>
    </div>
  );
};
